import React, { FC, PropsWithChildren, ReactNode, useMemo } from 'react';
import { createSignal } from '@funtech-int/rival';
import { useSignal } from '@funtech-int/rival-react';
import { useStorage } from '@funtech-int/web-toolkit';
import { useAuthManager } from '@funtech-int/yango-shared';

import { useHasSubscription } from '@/shared/hooks/useHasSubscription';
import { useAutoOpenPaymentWidgetParams } from '@/shared/hooks/useAutoOpenPaymentWidgetParams';
import { getIsProfilesPageSuppressed } from '@/app/lib/profilesPageSuppressed';
import {
    IS_PROFILE_FAMILY_CHOICE_SUPPRESSED_KEY,
    IS_PROFILE_FAMILY_CHOICE_SUPPRESSED_VALUE,
} from '@/shared/lib/profileSession';

import { ProfilesContent } from '../ProfilesContent/ProfilesContent';

import styles from './ProfilesScreen.module.css';

export interface ProfilesScreenProps {
    header: ReactNode;
}

export const ProfilesScreen: FC<PropsWithChildren<ProfilesScreenProps>> = ({
    header,
    children,
}) => {
    const { isAuthorized } = useAuthManager();
    const hasSubscription = useHasSubscription();
    const { shouldOpenWidgetFromQuery } = useAutoOpenPaymentWidgetParams();
    const storage = useStorage();

    const controller = useMemo(() => {
        const isPageSuppressed = getIsProfilesPageSuppressed(storage);

        const signal = createSignal(
            isAuthorized && hasSubscription && !isPageSuppressed && !shouldOpenWidgetFromQuery
        );

        return {
            signal,
            selectProfile() {
                signal.set(false);
            },
            hideProfiles() {
                signal.set(false);
                storage.local.set(
                    IS_PROFILE_FAMILY_CHOICE_SUPPRESSED_KEY,
                    IS_PROFILE_FAMILY_CHOICE_SUPPRESSED_VALUE
                );
            },
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const shouldShowProfiles = useSignal(controller.signal);

    if (shouldShowProfiles) {
        return (
            <>
                {header}
                <div className={styles.content} data-tid="ProfilesScreen">
                    <ProfilesContent
                        onHideProfilesChoice={controller.hideProfiles}
                        onChooseParentAccount={controller.selectProfile}
                    />
                </div>
            </>
        );
    }

    return children;
};
